import { AvailableFilter } from "@/models/filter";
import { organizeFieldsByPriority } from "@/utils/normalized-model-fields";
import { Control, FieldValues, Path, useForm, PathValue } from "react-hook-form";
import FieldItem from "@/features/filters/filter-content/field-item";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import useTranslation from "@/hooks/use-translation";
import { Z_INDEX } from "@/utils/z-index";
import { createPortal } from "react-dom";
import { getDisplayName, isPrefixedField } from "@/utils/fields-mapping";

interface FieldSelectorProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  availableFields: AvailableFilter[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  width?: string;
  onChangeSideEffect?: () => void;
  defaultValue?: string;
  props?: any;
  filterByTypes?: string[]; // Optionnel: filtrer les champs par types
}

interface FieldNode {
  name: string;
  fullPath: string;
  children: Map<string, FieldNode>;
  field?: AvailableFilter; // Le champ complet si c'est une feuille
}

/**
 * Construit un arbre hiérarchique à partir des champs
 * Utilise le nom d'affichage (sans préfixe type$) pour la hiérarchie
 * mais conserve la clé complète (type$name) dans le champ
 */
function buildFieldTree(fields: AvailableFilter[]): Map<string, FieldNode> {
  const root = new Map<string, FieldNode>();

  fields.forEach((field) => {
    // Extraire le nom d'affichage pour construire la hiérarchie
    const displayName = isPrefixedField(field.key) 
      ? getDisplayName(field.key) 
      : field.key;
    const parts = displayName.split(".");
    let currentLevel = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLeaf = i === parts.length - 1;

      if (!currentLevel.has(part)) {
        currentLevel.set(part, {
          name: part,
          fullPath: parts.slice(0, i + 1).join("."),
          children: new Map(),
          field: isLeaf ? field : undefined, // Conserver le champ complet avec type$name
        });
      }

      const node = currentLevel.get(part)!;
      
      if (isLeaf) {
        node.field = field; // Conserver le champ complet avec type$name
      }

      currentLevel = node.children;
    }
  });

  return root;
}

/**
 * Trie récursivement les nœuds de l'arbre
 */
function sortFieldTree(nodes: Map<string, FieldNode>): Map<string, FieldNode> {
  const sorted = new Map<string, FieldNode>();
  const entries = Array.from(nodes.entries()).sort(([a], [b]) => a.localeCompare(b));
  
  entries.forEach(([key, node]) => {
    sorted.set(key, {
      ...node,
      children: sortFieldTree(node.children),
    });
  });

  return sorted;
}

/**
 * Collecte tous les chemins des nœuds qui ont des enfants (pour les développer par défaut)
 */
function collectAllNodePaths(nodes: Map<string, FieldNode>): string[] {
  const paths: string[] = [];
  
  nodes.forEach((node) => {
    if (node.children.size > 0) {
      paths.push(node.fullPath);
      paths.push(...collectAllNodePaths(node.children));
    }
  });
  
  return paths;
}

/**
 * Composant pour un nœud de l'arbre avec fonctionnalité de collapse/expand
 */
const TreeNode: React.FC<{
  node: FieldNode;
  level: number;
  expandedNodes: Set<string>;
  onToggle: (path: string) => void;
  onFieldSelect?: (value: string) => void;
}> = ({ node, level, expandedNodes, onToggle, onFieldSelect }) => {
  const hasChildren = node.children.size > 0;
  const isExpanded = expandedNodes.has(node.fullPath);
  const indent = level * 20; // 20px d'indentation par niveau

  if (hasChildren) {
    // C'est un groupe avec des enfants
    // Pour les groupes, afficher simplement le nom du nœud (pas le chemin complet)
    const groupDisplayName = node.name;
    
    return (
      <React.Fragment key={node.fullPath}>
        {/* En-tête du groupe (cliquable pour expand/collapse) */}
        <div
          className="flex items-center justify-between w-full px-3 py-2 cursor-pointer hover:bg-surface-secondary transition-colors"
          style={{ paddingLeft: `${indent + 12}px` }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.fullPath);
          }}
        >
          <span className="text-sm font-medium text-content-primary">{groupDisplayName}</span>
          <span className="text-xs text-content-tertiary ml-2">
            {isExpanded ? "▼" : "▶"}
          </span>
        </div>

        {/* Enfants (affichés seulement si le nœud est expandé) */}
        {isExpanded &&
          Array.from(node.children.entries()).map(([key, childNode]) => (
            <TreeNode
              key={childNode.fullPath}
              node={childNode}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onFieldSelect={onFieldSelect}
            />
          ))}
      </React.Fragment>
    );
  } else if (node.field) {
    // C'est une feuille, afficher le champ
    return (
      <div
        key={node.field.key}
        className="flex items-center w-full px-3 py-2 cursor-pointer hover:bg-accent/10 transition-colors"
        style={{ paddingLeft: `${indent + 12}px` }}
        onClick={(e) => {
          e.stopPropagation();
          if (onFieldSelect) {
            onFieldSelect(node.field!.key);
          }
        }}
      >
        <FieldItem field={node.field.key} type={node.field.type} />
      </div>
    );
  }

  return null;
};

/**
 * Composant de sélection de champs qui priorise les champs du schéma NormalizedModel
 * et affiche les autres champs dans une section séparée
 */
export const FieldSelector = <T extends FieldValues>({
  control,
  name,
  availableFields,
  label,
  placeholder = "choosefilter",
  required = false,
  width = "100%",
  onChangeSideEffect,
  defaultValue = "",
  props,
  filterByTypes,
}: FieldSelectorProps<T>) => {
  const { t } = useTranslation();
  
  // État pour gérer les nœuds expandés/collapsés
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  
  // État pour contrôler l'ouverture du menu
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Refs pour le positionnement du dropdown
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // Fonction pour toggle l'état d'un nœud
  const toggleNode = (path: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Filtrer les champs par types si nécessaire
  const filteredFields = useMemo(() => {
    if (filterByTypes && filterByTypes.length > 0) {
      return availableFields.filter((field) =>
        filterByTypes.includes(field.type)
      );
    }
    return availableFields;
  }, [availableFields, filterByTypes]);

  // Organiser les champs en prioritaires et autres
  // Note: On affiche tous les champs disponibles, pas seulement ceux avec facets
  const { priorityFields, otherFields } = useMemo(() => {
    return organizeFieldsByPriority(filteredFields);
  }, [filteredFields]);
  
  // Tous les champs filtrés sont affichés
  const fieldsToDisplay = filteredFields;

  // Construire l'arbre hiérarchique pour les champs prioritaires
  const priorityTree = useMemo(() => {
    const tree = buildFieldTree(priorityFields);
    return sortFieldTree(tree);
  }, [priorityFields]);

  // Construire l'arbre hiérarchique pour les autres champs
  const otherTree = useMemo(() => {
    const tree = buildFieldTree(otherFields);
    return sortFieldTree(tree);
  }, [otherFields]);

  // Collecter tous les chemins des nœuds pour les développer par défaut
  const allNodePaths = useMemo(() => {
    return new Set([
      ...collectAllNodePaths(priorityTree),
      ...collectAllNodePaths(otherTree),
    ]);
  }, [priorityTree, otherTree]);

  // Initialiser expandedNodes avec tous les chemins quand les arbres changent
  useEffect(() => {
    if (allNodePaths.size > 0 && expandedNodes.size === 0) {
      setExpandedNodes(allNodePaths);
    }
  }, [allNodePaths]);

  // Créer une map pour trouver rapidement le type d'un champ
  const fieldTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    availableFields.forEach((field) => {
      map.set(field.key, field.type);
    });
    return map;
  }, [availableFields]);

  // Obtenir le premier champ disponible
  const firstAvailableField = useMemo(() => {
    if (fieldsToDisplay.length > 0) {
      // Prioriser les champs prioritaires s'ils existent
      if (priorityFields.length > 0) {
        return priorityFields[0].key;
      }
      // Sinon, prendre le premier champ disponible
      return fieldsToDisplay[0].key;
    }
    return "";
  }, [fieldsToDisplay, priorityFields]);

  // Référence pour suivre si on a déjà initialisé la valeur
  const hasInitializedRef = React.useRef(false);
  
  // Update dropdown position when opening
  useEffect(() => {
    if (menuOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [menuOpen]);
  
  // Close on click outside
  useEffect(() => {
    if (!menuOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={(defaultValue || firstAvailableField) as PathValue<T, Path<T>>}
      rules={{ required: required }}
      render={({ field, fieldState }) => {
        // Sélectionner automatiquement le premier champ si la valeur est vide et qu'un champ est disponible
        const FieldSelectorContent = () => {
          useEffect(() => {
            if (!hasInitializedRef.current && !field.value && firstAvailableField && (!defaultValue || defaultValue === "")) {
              hasInitializedRef.current = true;
              field.onChange(firstAvailableField);
              if (onChangeSideEffect) {
                onChangeSideEffect();
              }
            }
          }, []);
          
          return null;
        };
        
        const handleFieldSelect = (value: string) => {
          field.onChange(value);
          setMenuOpen(false);
          if (onChangeSideEffect) {
            onChangeSideEffect();
          }
        };
        
        return (
          <>
            <FieldSelectorContent />
            <div style={{ width }} className="relative">
              {label && (
                <label className="block text-sm font-medium text-content-primary mb-1.5">
                  {label}
                  {required && <span className="text-error ml-1">*</span>}
                </label>
              )}
              
              {/* Trigger Button */}
              <div
                ref={triggerRef}
                onClick={() => setMenuOpen(!menuOpen)}
                className={`
                  w-full flex items-center justify-between
                  px-3 py-2.5
                  bg-surface-elevated border rounded-lg
                  cursor-pointer
                  transition-all duration-200
                  ${menuOpen 
                    ? "border-accent ring-2 ring-accent/20" 
                    : fieldState.invalid 
                      ? "border-error" 
                      : "border-arc-border hover:border-arc-border-strong"
                  }
                `}
              >
                <div className="flex-1 min-w-0">
                  {field.value ? (
                    <FieldItem field={field.value} type={fieldTypeMap.get(field.value)} />
                  ) : (
                    <span className="text-content-tertiary">{t(placeholder)}</span>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-content-tertiary transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {/* Dropdown */}
              {menuOpen && typeof document !== "undefined" && createPortal(
                <div
                  ref={dropdownRef}
                  className="bg-surface-elevated border border-arc-border rounded-lg shadow-xl overflow-hidden animate-fadeIn"
                  style={{
                    position: "fixed",
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    maxHeight: 400,
                    overflowY: "auto",
                    zIndex: Z_INDEX.POPOVER,
                  }}
                >
                  {/* Section des champs prioritaires avec hiérarchie */}
                  {priorityFields.length > 0 &&
                    Array.from(priorityTree.entries()).map(([key, node]) => (
                      <TreeNode
                        key={node.fullPath}
                        node={node}
                        level={0}
                        expandedNodes={expandedNodes}
                        onToggle={toggleNode}
                        onFieldSelect={handleFieldSelect}
                      />
                    ))}

                  {/* Section des autres champs avec hiérarchie */}
                  {otherFields.length > 0 &&
                    Array.from(otherTree.entries()).map(([key, node]) => (
                      <TreeNode
                        key={node.fullPath}
                        node={node}
                        level={0}
                        expandedNodes={expandedNodes}
                        onToggle={toggleNode}
                        onFieldSelect={handleFieldSelect}
                      />
                    ))}
                </div>,
                document.body
              )}
            </div>
          </>
        );
      }}
    />
  );
};

export default FieldSelector;

/**
 * Version simplifiée de FieldSelector qui fonctionne sans react-hook-form
 * Utilise react-hook-form en interne mais expose une interface simple avec value/onChange
 */
interface FieldSelectorSimpleProps {
  availableFields: AvailableFilter[];
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  width?: string;
  props?: any;
  filterByTypes?: string[];
}

export const FieldSelectorSimple: React.FC<FieldSelectorSimpleProps> = ({
  availableFields,
  value = "",
  onChange,
  label,
  placeholder = "choosefilter",
  required = false,
  width = "100%",
  props,
  filterByTypes,
}) => {
  // Filtrer les champs par types si nécessaire
  const filteredFields = useMemo(() => {
    if (filterByTypes && filterByTypes.length > 0) {
      return availableFields.filter((field) =>
        filterByTypes.includes(field.type)
      );
    }
    return availableFields;
  }, [availableFields, filterByTypes]);

  // Obtenir le premier champ disponible
  const firstAvailableField = useMemo(() => {
    if (filteredFields.length > 0) {
      // Organiser les champs par priorité
      const { priorityFields } = organizeFieldsByPriority(filteredFields);
      // Prioriser les champs prioritaires s'ils existent
      if (priorityFields.length > 0) {
        return priorityFields[0].key;
      }
      // Sinon, prendre le premier champ disponible
      return filteredFields[0].key;
    }
    return "";
  }, [filteredFields]);

  const { control, setValue, watch } = useForm({
    defaultValues: { field: value || firstAvailableField },
  });
  const prevValueRef = React.useRef(value);
  const isInternalChangeRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);

  // Sélectionner automatiquement le premier champ si la valeur est vide au montage
  useEffect(() => {
    if (!hasInitializedRef.current && !value && firstAvailableField) {
      hasInitializedRef.current = true;
      setValue("field", firstAvailableField);
      if (onChange) {
        onChange(firstAvailableField);
      }
    }
  }, [value, firstAvailableField, setValue, onChange]);

  // Synchroniser la valeur externe avec le formulaire interne
  useEffect(() => {
    if (value !== prevValueRef.current && !isInternalChangeRef.current) {
      setValue("field", value);
      prevValueRef.current = value;
    }
    isInternalChangeRef.current = false;
  }, [value, setValue]);

  // Écouter les changements du formulaire et notifier le parent
  const watchedValue = watch("field");
  useEffect(() => {
    if (watchedValue !== prevValueRef.current && onChange) {
      isInternalChangeRef.current = true;
      prevValueRef.current = watchedValue;
      onChange(watchedValue);
    }
  }, [watchedValue, onChange]);

  return (
    <FieldSelector
      control={control}
      name="field"
      availableFields={availableFields}
      label={label}
      placeholder={placeholder}
      required={required}
      width={width}
      props={props}
      filterByTypes={filterByTypes}
    />
  );
};
